import { useQuery } from '@tanstack/react-query';
import { API_URL } from '@/config/api';

export interface MatomoStats {
  visits: number;
  uniqueVisitors: number;
  pageViews: number;
  bounceRate: number;
  avgVisitDuration: number;
  conversionRate: number;
}

export interface MatomoPageStats {
  label: string;
  nb_visits: number;
  nb_hits: number;
  bounce_rate: string;
  avg_time_on_page: number;
}

export interface MatomoVisitorStats {
  date: string;
  nb_visits: number;
  nb_uniq_visitors: number;
  nb_pageviews: number;
}

export interface PageMindUsageStats {
  visitsWithPagemind: number;
  totalVisits: number;
  usagePercent: number;
  avgDurationWithPagemind: number;
  avgDurationWithoutPagemind: number;
  durationChangePercent: number;
}

class MatomoService {
  private async fetchFromAPI(endpoint: string, params: Record<string, any> = {}) {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${API_URL}${endpoint}${queryParams ? `?${queryParams}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.statusText}`);
    }

    return response.json();
  }

  async getSiteStats(siteUrl: string, period: string = 'day', date: string = 'today'): Promise<MatomoStats> {
    try {
      const data = await this.fetchFromAPI('/analytics/matomo/stats', {
        siteUrl,
        period,
        date,
      });
      
      return {
        visits: data.nb_visits || 0,
        uniqueVisitors: data.nb_uniq_visitors || 0,
        pageViews: data.nb_pageviews || 0,
        bounceRate: parseFloat(data.bounce_rate || '0'),
        avgVisitDuration: data.avg_visit_duration || 0,
        conversionRate: parseFloat(data.conversion_rate || '0'),
      };
    } catch (error) {
      console.error('Error fetching Matomo stats:', error);
      throw error;
    }
  }

  async getPageStats(siteUrl: string, period: string = 'day', date: string = 'today'): Promise<MatomoPageStats[]> {
    try {
      const data = await this.fetchFromAPI('/analytics/matomo/pages', {
        siteUrl,
        period,
        date,
      });
      
      return data || [];
    } catch (error) {
      console.error('Error fetching Matomo page stats:', error);
      throw error;
    }
  }

  async getVisitorTrend(siteUrl: string, period: string = 'day', date: string = 'last7'): Promise<MatomoVisitorStats[]> {
    try {
      const data = await this.fetchFromAPI('/analytics/matomo/visitors', {
        siteUrl,
        period,
        date,
      });
      
      return data || [];
    } catch (error) {
      console.error('Error fetching Matomo visitor trend:', error);
      throw error;
    }
  }

  async getPageMindUsage(siteUrl: string, period: string = 'day', date: string = 'today'): Promise<PageMindUsageStats> {
    try {
      const data = await this.fetchFromAPI('/analytics/matomo/pagemind-usage', {
        siteUrl,
        period,
        date,
      });
      
      return data;
    } catch (error) {
      console.error('Error fetching PageMind usage stats:', error);
      throw error;
    }
  }
}

export const matomoService = new MatomoService();

// React Query hooks
export const useMatomoStats = (siteUrl?: string, period: string = 'day', date: string = 'today') => {
  return useQuery({
    queryKey: ['matomo-stats', siteUrl, period, date],
    queryFn: () => matomoService.getSiteStats(siteUrl!, period, date),
    enabled: !!siteUrl,
    refetchInterval: 60000, // Refetch every minute
  });
};

export const useMatomoPageStats = (siteUrl?: string, period: string = 'day', date: string = 'today') => {
  return useQuery({
    queryKey: ['matomo-pages', siteUrl, period, date],
    queryFn: () => matomoService.getPageStats(siteUrl!, period, date),
    enabled: !!siteUrl,
  });
};

export const useMatomoVisitorTrend = (siteUrl?: string, period: string = 'day', date: string = 'last7') => {
  return useQuery({
    queryKey: ['matomo-visitors', siteUrl, period, date],
    queryFn: () => matomoService.getVisitorTrend(siteUrl!, period, date),
    enabled: !!siteUrl,
  });
};

export const usePageMindUsage = (siteUrl?: string, period: string = 'day', date: string = 'today') => {
  return useQuery({
    queryKey: ['pagemind-usage', siteUrl, period, date],
    queryFn: () => matomoService.getPageMindUsage(siteUrl!, period, date),
    enabled: !!siteUrl,
    refetchInterval: 60000, // Refetch every minute
  });
};